// The query parameters carried by every Try It Now landing URL:
//   demo=<solutionCode>   which solution's demo this is
//   view=own|sample       whether it is showing the prospect's real team, or the synthetic fixture
//
// Userpilot targets tours by page URL, which is identity-independent: unlike a user property it does
// not depend on identify() reaching the server, on which profile is active (the real employeeId vs
// the synthetic preview id), or on surviving the Userpilot.reset() that runs on preview boot. The
// demo value is the KAP-configured DemoTargetCode with its `try/` prefix stripped — the same
// solutionCode held in the preview intent. `view` lets a tour distinguish the two datasets, which
// the solution code alone cannot: both modes run under the same solution.
//
// A second, narrower kind of destination also carries `demo=`: a LINK demo (see LINK_DEMO_PATHS) is
// an ordinary page on the org's own data with no sandbox behind it, so its param is un-armed by
// definition and `view` never applies.
//
// Param ORDER is not stable — a param already on the URL keeps its position, a new one is appended —
// so Userpilot page rules must match on "URL contains", never on an exact URL.
//
// Kept dependency-free, and pure with the values injected rather than read from arming state, so App
// and the Team page can import it without pulling in the preview runtime (mock, datasets) for what
// is only a string helper — and so it is trivially testable.

const DEMO_PARAM = 'demo';
const VIEW_PARAM = 'view';

/** Demo code for the "Accredited Airport Training" destination (My Klayo ▸ Courses). */
export const ACCREDITED_AIRPORT_TRAINING_DEMO = 'accredited-airport-training';

/**
 * "Link demo" destinations — demo code → the ONE real page it belongs on.
 *
 * These are Try It Now destinations that simply navigate to a real page on the org's own data
 * (SolutionCard dispatches them alongside JobBuilder / JobBenchmarking), with no sandbox to arm.
 * They still carry `demo=` for the reason any demo URL does — Userpilot targets tours by URL, so
 * the param is what keeps the tour from firing on every ordinary visit to that page — but there is
 * no arming state for the correction to derive it from, so the strip below has to exempt it.
 *
 * The path is what BOUNDS that exemption. Without it the param would survive on every route, so a
 * forwarded `/settings/billing?demo=…` would keep matching a "URL contains" tour rule on a real
 * customer's session — precisely what the strip exists to prevent.
 */
const LINK_DEMO_PATHS: Record<string, string> = {
  [ACCREDITED_AIRPORT_TRAINING_DEMO]: '/my-klayo/courses',
};

/** The landing URL a link demo's Try It Now button navigates to. */
export function linkDemoUrl(code: string): string {
  return `${LINK_DEMO_PATHS[code]}?${DEMO_PARAM}=${code}`;
}

/** What the sandbox is showing: the prospect's own team, or the synthetic sample. */
export type DemoView = 'own' | 'sample';

/**
 * The params an armed session should be carrying — or null when this is not a demo. A union rather
 * than nullable fields, so "half a demo" is unrepresentable and callers get one branch, not two.
 */
export type DemoUrlState = { solutionCode: string; view: DemoView } | null;

/** Add (or replace) the demo params on a path that may already carry a query string. */
export function withDemoParam(path: string, solutionCode: string, view: DemoView): string {
  if (!solutionCode) return path;
  const [base, query] = path.split('?');
  const params = new URLSearchParams(query);
  params.set(DEMO_PARAM, solutionCode);
  params.set(VIEW_PARAM, view);
  return `${base}?${params.toString()}`;
}

/**
 * The URL this session should be showing, or null when it already is.
 *
 * Pages rebuild their own URLs constantly — Team reserializes its query from the filter model, the
 * tab strip and app nav build bare NavLink targets — so threading the params through each of them is
 * a losing game: one missed rebuild and the tour targeting is dead for the rest of the session.
 * Re-deriving them from arming state on every navigation covers them all at one point.
 *
 * It corrects in both directions, which makes "these params are present exactly when the session is
 * armed — or it is a link demo on its own page" an invariant: any OTHER demo URL forwarded to a real
 * customer (pasted link, bookmark) is stripped rather than followed around, so they never match a
 * prospect's tour and their page views stay out of the demo traffic. The link-demo carve-out is
 * deliberately the narrowest shape that still works: one code, alone, on one path — see
 * LINK_DEMO_PATHS. Stripping in place, rather than redirecting off the page, is deliberate — an
 * un-armed demo URL is also how the documented "Show my team" read-failure fallback lands the user
 * on the normal Team page (bootstrapPreview), and bouncing them would break it.
 */
export function demoParamCorrection(
  pathname: string,
  search: string,
  state: DemoUrlState,
  hash = ''
): string | null {
  const params = new URLSearchParams(search);
  // getAll, not get: a repeated `?demo=a&demo=b` reports only `a` through get(), so every check
  // below counts the values rather than reading the first one. Otherwise a single trusted value
  // would vouch for whatever was appended behind it.
  const demos = params.getAll(DEMO_PARAM);

  if (!state) {
    // `view` is a generic name, so only claim it alongside `demo` — a real page free to use `?view=`
    // for its own purposes keeps it, and only a genuine demo URL is cleaned up.
    if (!demos.length) return null;

    // A link demo legitimately rides an un-armed session: it IS the real app, and the param is what
    // scopes its tour to arrivals from Try It Now. Exempt it only when it is the SOLE demo value and
    // this is the page it belongs to — both conditions bound the hole to the feature. `view` means
    // "which sandbox dataset", so it is never part of a link demo and is stripped either way.
    const isLinkDemo = demos.length === 1 && LINK_DEMO_PATHS[demos[0]] === pathname;
    if (isLinkDemo && !params.has(VIEW_PARAM)) return null;

    if (!isLinkDemo) params.delete(DEMO_PARAM);
    params.delete(VIEW_PARAM);
    const query = params.toString();
    return (query ? `${pathname}?${query}` : pathname) + hash;
  }

  // An armed session owns both params outright. Length check for the same reason as above: a
  // duplicate would survive a first-value match, and withDemoParam's set() collapses it anyway.
  if (
    demos.length === 1 &&
    demos[0] === state.solutionCode &&
    params.get(VIEW_PARAM) === state.view
  )
    return null;
  return withDemoParam(pathname + search, state.solutionCode, state.view) + hash;
}
